import SectionHeader from '../../components/ui/section-header';
import ProfileViewModal from '../../components/module/my-team/modal/profile-view-modal';
import { departmentTypesForApi } from '../../api/user/user-api';
import { useDepartmentUserAccess } from '../../hooks/access/use-department-user-access';
import { useMyTeamDataState } from '../../hooks/data-state/use-my-team-data-state';
import { useDispatch } from 'react-redux';
import { setPrimaryModalMyTeam, setSearchValueMyTeam, setUserIdMyTeam } from '../../features/my-team/my-team-slice';
import TableEmpty from '../../components/ui/table-empty';
import TableLoading from '../../components/ui/table-loading';
import TableError from '../../components/ui/table-error';
import MyTeamTHead from '../../components/module/my-team/common/my-team-thead';

interface Props {
    departmentType: departmentTypesForApi;
}

export default function MyTeam({ departmentType }: Props) {

    const dispatch = useDispatch()
    const {
        filteredUsers: gmsUsers,
        departmentCount,
        getDepartmentUserById,
        isLoadingDepartmentUsers,
        isErrorDepartmentUsers,
    } = useDepartmentUserAccess({departmentType})
    const {
        isShowViewProfilePrimaryMyTeam,
        searchValueMyTeam,
        userIdMyTeam,
    } = useMyTeamDataState()

    const handleViewUser = (userId: number) => {
        dispatch(setPrimaryModalMyTeam('viewProfile'))
        dispatch(setUserIdMyTeam(userId))
    }

    const inputStyle = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs text-gray-700 outline-none transition focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:bg-gray-100';
    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-[#f8fafc] rounded-xl border border-gray-200">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <SectionHeader
                    title='My Team'
                    subtitle='View your team members and monitor their information and status.'
                    icon={
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"> 
                            <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /> 
                            <circle cx="10" cy="7" r="4" /> 
                            <path d="M20 21v-2a4 4 0 0 0-3-3.87" /> 
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" /> 
                        </svg>
                    }
                />
                <div className="mx-auto flex min-h-0 w-full flex-1 flex-col gap-2">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white shadow-sm">
                        <div className="grid shrink-0 grid-cols-3 gap-1.5 p-2 sm:gap-2 sm:p-3">
                            <div className="flex min-w-0 flex-col justify-center rounded-lg border border-gray-200 bg-white px-2 py-2 shadow-sm sm:px-3 sm:py-3">
                                <p className="truncate text-[9px] font-medium uppercase tracking-wide text-gray-400 sm:text-[10px]">
                                    Total
                                </p>
                                <p className="mt-0.5 text-base font-semibold leading-tight text-[#186cb2] sm:mt-1 sm:text-lg">
                                    {departmentCount?.total ?? 0}
                                </p>
                            </div>
                            <div className="flex min-w-0 flex-col justify-center rounded-lg border border-gray-200 bg-white px-2 py-2 shadow-sm sm:px-3 sm:py-3">
                                <p className="truncate text-[9px] font-medium uppercase tracking-wide text-gray-400 sm:text-[10px]">
                                    Active
                                </p>
                                <p className="mt-0.5 text-base font-semibold leading-tight text-green-600 sm:mt-1 sm:text-lg">
                                    {departmentCount?.totalActive ?? 0}
                                </p>
                            </div>
                            <div className="flex min-w-0 flex-col justify-center rounded-lg border border-gray-200 bg-white px-2 py-2 shadow-sm sm:px-3 sm:py-3">
                                <p className="truncate text-[9px] font-medium uppercase tracking-wide text-gray-400 sm:text-[10px]">
                                    Inactive
                                </p>
                                <p className="mt-0.5 text-base font-semibold leading-tight text-red-600 sm:mt-1 sm:text-lg">
                                    {departmentCount?.totalInactive ?? 0}
                                </p>
                            </div>
                        </div>
                        <div className="p-2 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:items-center lg:justify-between">
                            <div className="relative min-w-0 sm:w-full sm:max-w-md sm:flex-1">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400">
                                    <circle cx="11" cy="11" r="7" />
                                    <path strokeLinecap="round" d="m16.5 16.5 4 4" />
                                </svg>
                                <input
                                    type="text"
                                    value={searchValueMyTeam}
                                    onChange={(event) => dispatch(setSearchValueMyTeam(event.target.value))}
                                    placeholder="Search name, location, email, number, or status..."
                                    className={`${inputStyle} min-w-0 pl-9`}
                                />
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-auto border-t bg-white shadow-sm">
                            <table className="min-w-[900px] w-full border-collapse">
                                <MyTeamTHead/>
                                <tbody>
                                    {isLoadingDepartmentUsers ? (
                                        <TableLoading/>
                                    ) : isErrorDepartmentUsers ? (
                                        <TableError/>
                                    ) : gmsUsers.length === 0 && searchValueMyTeam.length !== 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-3 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#eef6fc] text-[#186cb2]">
                                                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                            <circle cx="11" cy="11" r="7" />
                                                            <path d="m20 20-4-4" />
                                                        </svg>
                                                    </div>
                                                    <p className="mt-2 text-xs font-medium text-gray-600">
                                                        No users found
                                                    </p>
                                                    <p className="mt-1 text-[10px] text-gray-400">
                                                        Try adjusting your search.
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : gmsUsers.length === 0 ? (
                                        <TableEmpty/>
                                    ) : (
                                        gmsUsers.map((user, index) => (
                                            <tr
                                                // key={user.department_id}
                                                key={index}
                                                className="border-b border-gray-100 transition hover:bg-[#eef6fc]/50"
                                            >
                                                <td className="px-3 py-3 text-xs font-medium text-gray-700">
                                                    {user.name ?? '-'}
                                                </td>
                                                <td className="px-3 py-3 text-xs text-gray-600">
                                                    {user.location_name ?? '-'}
                                                </td>
                                                <td className="px-3 py-3 text-xs text-gray-600">
                                                    {user.email ?? '-'}
                                                </td>
                                                <td className="px-3 py-3 text-xs text-gray-600">
                                                    {/* {user. || "-"} */}
                                                    {user.phoneNumber ?? '-'}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <span className={`inline-flex rounded-full px-2 py-1 text-[9px] font-semibold 
                                                        ${user.is_active  ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500" }`}
                                                    >
                                                        {user.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewUser(user.user_id)}
                                                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[#186cb2]/20 bg-white px-2.5 text-[11px] font-semibold text-[#186cb2] transition hover:border-[#186cb2]/40 hover:bg-[#186cb2]/5 active:scale-95"
                                                    >
                                                        <svg
                                                            className="h-3.5 w-3.5"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="1.8"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                                                            <circle cx="12" cy="12" r="2.5" />
                                                        </svg>
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <ProfileViewModal
                isOpen={isShowViewProfilePrimaryMyTeam}
                onClose={() => {    
                    dispatch(setPrimaryModalMyTeam(null))
                    dispatch(setUserIdMyTeam(null))
                }}
                userId={userIdMyTeam}
                getDepartmentUserById={getDepartmentUserById}
            />
        </div>
    );
}